import React from 'react';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import { AccountStatus } from '../../api/admin';
import { formatDate } from '../../utils/dates';

/**
 * Colour language: green = paying now, amber = trial running, blue = prospect,
 * red = needs attention (expired trial or term, or no account),
 * grey = over (churned / lost).
 */
const STATUS: Record<AccountStatus, { label: string; color: 'success' | 'warning' | 'error' | 'info' | 'default' }> = {
	prospect: { label: 'Prospect', color: 'info' },
	trial: { label: 'Trial', color: 'warning' },
	live: { label: 'Live', color: 'success' },
	trial_expired: { label: 'Trial (!)', color: 'error' },
	expired: { label: 'Expired', color: 'error' },
	churned: { label: 'Churned', color: 'default' },
	lost: { label: 'Lost', color: 'default' },
};

/** The chip's word for a status, e.g. 'Live', for places that show it as text. */
export function statusLabel(status: AccountStatus): string {
	return STATUS[status].label;
}

/** The chip's colour as an sx colour, e.g. 'success.main', for text that should match the chip. */
export function statusTextColor(status: AccountStatus): string {
	const color = STATUS[status].color;
	return color === 'default' ? 'text.secondary' : `${color}.main`;
}

/** What the chip needs; satisfied by both an account row and a user's account ref. */
export interface AccountLike {
	status: AccountStatus;
	start_date: string | null;
	end_date: string | null;
	team_name?: string;
	account_name?: string;
}

/** One-line explanation, e.g. "Trial via Tech Desk · ends 2026-08-27". */
export function accountSummary(a: AccountLike): string {
	const via = a.team_name ? ` via ${a.team_name}` : '';
	const team = a.team_name ? ` (${a.team_name})` : '';
	switch (a.status) {
		case 'prospect':
			return `Prospect${via}`;
		case 'trial':
			return `Trial${via} · ${a.end_date ? `ends ${formatDate(a.end_date)}` : 'no end date'}`;
		case 'live':
			return `Live${via}${a.start_date ? ` · from ${formatDate(a.start_date)}` : ''} · ${
				a.end_date ? `ends ${formatDate(a.end_date)}` : 'open-ended'
			}`;
		case 'trial_expired':
			return `Trial ended ${formatDate(a.end_date)}${team}`;
		case 'expired':
			return `Term ended ${formatDate(a.end_date)}${team}`;
		case 'churned':
			return `Churned${team}`;
		case 'lost':
			return `Lost${team}`;
	}
}

interface Props {
	account: AccountLike | null | undefined;
	/** vendors are not expected to have an account, so "none" is grey rather than red for them */
	isVendor?: boolean;
}

export default function AccountStatusChip({ account, isVendor = false }: Props) {
	if (!account && isVendor) {
		return (
			<Tooltip title="Vendor user, no account expected">
				<Chip size="small" variant="outlined" label="Vendor" color="default" />
			</Tooltip>
		);
	}
	if (!account) {
		return (
			<Tooltip title="No account covers this user's teams">
				<Chip size="small" variant="outlined" label="None" color="error" />
			</Tooltip>
		);
	}
	const cfg = STATUS[account.status];
	return (
		<Tooltip title={accountSummary(account)}>
			<Chip size="small" variant="outlined" label={cfg.label} color={cfg.color} />
		</Tooltip>
	);
}
